import axios, { type AxiosRequestConfig, type Method } from 'axios';
import { md5 } from 'js-md5';
import { API_BASE_URL, DEFAULT_TIMEOUT, NETWORK_ERROR_MESSAGE, SIGN_KEY } from './config';
import { getAuthProvider } from './auth-provider';
import type { ApiResponse, HttpRequestOptions } from './types';
import { getFlowPermission } from '../config/flow-permission-store';
import { toast } from 'sonner';

type RequestPayload = Record<string, unknown> | undefined;

const httpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
});

function createNetworkErrorResponse<T>(): ApiResponse<T> {
  return {
    code: -1,
    msg: NETWORK_ERROR_MESSAGE,
  };
}

function buildCommonParams(withSignature: boolean, signatureUuid?: string) {
  const provider = getAuthProvider();
  const token = provider.getToken();
  const providerUuid = provider.getUuid() || '';
  const teamId = provider.getTeamId?.() || '';
  const uuid = signatureUuid?.trim() || providerUuid.trim();

  const params: Record<string, unknown> = {
    token,
    op_team: teamId,
    platform: 'web',
    app: 'hb',
  };

  if (withSignature && uuid) {
    const nonce = Math.random().toString(36).slice(2);
    const ts = Date.now();
    params.nonce = nonce;
    params.ts = ts;
    params.sign = md5(`${SIGN_KEY}${uuid}${nonce}${ts}`);
  }

  return params;
}

async function request<T>(
  method: Method,
  url: string,
  payload?: RequestPayload,
  options?: HttpRequestOptions,
): Promise<ApiResponse<T>> {
  if (getFlowPermission().isReadonly) {
    const allowedUrls = [
      '/zs/AiCanvasProject/getProjectDetail',
      '/zs/AiCanvasProject/getShareDetail',
      '/zs/AiCanvasTask/getTaskModel',
      '/zs/AiCanvasTask/getPointRuleConfig',
      '/zs/AiCanvasProject/applyEditPermission',
      '/zs/user/refreshWxLogin',
      '/zs/user/login',
      '/zs/user/getPcLoginQR',
      '/zs/AiCanvasProject/clonePublicProject',
      '/zs/User/getCollabToken',
      '/zs/AiCanvasMaterial/aiGenerateRecord',
      '/zs/AiCanvasTeam/teamList',
      '/zs/AiCanvasTeam/enterTeam',
      '/zs/AiCanvasTeam/getCurrentTeam',
      '/zs/AiCanvasProject/saveProject',
    ];
    if (!allowedUrls.some(allowed => url.includes(allowed))) {
      console.warn(`[HTTP Blocked] API call to ${url} blocked in readonly mode`);
      const msg = '只读模式，无法进行该操作';
      return {
        code: -999,
        msg,
      } as ApiResponse<T>;
    }
  }

  const withCommonParams = options?.withCommonParams ?? true;
  const withSignature = options?.withSignature ?? true;
  const mergedPayload = withCommonParams
    ? { ...buildCommonParams(withSignature, options?.signatureUuid), ...(payload ?? {}) }
    : (payload ?? {});

  const config: AxiosRequestConfig = {
    method,
    url,
    timeout: options?.timeout,
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers ?? {}),
    },
  };

  if (method.toUpperCase() === 'GET') {
    config.params = mergedPayload;
  } else {
    config.data = mergedPayload;
  }

  try {
    const response = await httpClient.request<ApiResponse<T>>(config);
    const result = response.data;

    if (result?.code === -1000) {
      getAuthProvider().onAuthExpired?.();
    } else if (result?.code == -1 && result.msg == '登录信息异常，请重新登录') {
      getAuthProvider().onAuthExpired?.();
    }

    return result;
  } catch {
    return createNetworkErrorResponse<T>();
  }
}

export { httpClient };
export const httpRequest = request;
